import { Platform } from 'react-native';
import {
  fetchProducts,
  requestPurchase,
  finishTransaction,
  getAvailablePurchases,
  deepLinkToSubscriptions,
  type Purchase,
  type ProductSubscriptionAndroid,
} from 'expo-iap';
import api from './api';
import { getApiErrorMessage } from '@/utils/api-error';

const PACKAGE_NAME = 'com.controlapag.app';

/**
 * Assinaturas no Google Play → plano interno. Mesmos IDs da App Store, menos o Pro (limite de 40 caracteres do Google); a API
 * tem a mesma tabela em `api/src/google-play/google-play.products.ts`.
 */
export const GOOGLE_PLAY_PRODUCT_IDS: Record<string, string> = {
  basic: 'com.controlapag.subscription.basic',
  professional: 'com.controlapag.subscription.pro',
  enterprise: 'com.controlapag.subscription.enterprise',
};

/** Oferta de boas-vindas do Pro (1º mês por R$ 1,99), só para quem nunca assinou. */
export const GOOGLE_INTRO_OFFER_ID = 'primeiro-mes';

/** Troca de plano vale na hora, com o crédito do tempo que sobrou (`WITH_TIME_PRORATION`). */
const REPLACEMENT_WITH_TIME_PRORATION = 1;

export function isGooglePlayAvailable(): boolean {
  return Platform.OS === 'android';
}

export function getGoogleProductId(planId: string): string | null {
  return GOOGLE_PLAY_PRODUCT_IDS[planId] ?? null;
}

export function getPlanIdForGoogleProductId(productId: string): string | null {
  const entry = Object.entries(GOOGLE_PLAY_PRODUCT_IDS).find(([, sku]) => sku === productId);
  return entry ? entry[0] : null;
}

/** O Google exige o `offerToken` do plano básico para abrir a compra. */
async function offerTokenFor(sku: string): Promise<string> {
  const products = (await fetchProducts({ skus: [sku], type: 'subs' })) as
    | ProductSubscriptionAndroid[]
    | null;
  const offers = products?.find((p) => p.id === sku)?.subscriptionOfferDetailsAndroid ?? [];
  // O Google só lista a oferta de boas-vindas para quem tem direito; como na
  // Apple, ela vale sozinha. Sem `offerId` = o plano básico, sem promoção.
  const offer =
    offers.find((o) => o.offerId === GOOGLE_INTRO_OFFER_ID) ??
    offers.find((o) => !o.offerId) ??
    offers[0];
  if (!offer) {
    throw new Error('Plano indisponível no Google Play no momento.');
  }
  return offer.offerToken;
}

export interface GooglePlayPurchaseResult {
  ok: boolean;
  planId?: string;
  error?: string;
}

/**
 * Abre a compra no Google Play. Com uma assinatura do Google em andamento,
 * troca de plano em vez de abrir uma segunda assinatura. O id do usuário vai
 * junto para a API conferir de quem é a compra.
 */
export async function requestGoogleSubscription(
  planId: string,
  options: { userId?: string; currentPurchaseToken?: string | null },
): Promise<void> {
  const sku = getGoogleProductId(planId);
  if (!sku) {
    throw new Error(`Produto do Google Play não configurado para o plano: ${planId}`);
  }
  const offerToken = await offerTokenFor(sku);

  await requestPurchase({
    type: 'subs',
    request: {
      google: {
        skus: [sku],
        subscriptionOffers: [{ sku, offerToken }],
        ...(options.userId ? { obfuscatedAccountId: options.userId } : {}),
        ...(options.currentPurchaseToken
          ? {
              purchaseToken: options.currentPurchaseToken,
              replacementMode: REPLACEMENT_WITH_TIME_PRORATION,
            }
          : {}),
      },
    },
  });
}

/** Valida na API (que também confirma a compra no Google) e encerra a transação. */
export async function validateGooglePurchaseWithBackend(
  purchase: Purchase,
): Promise<GooglePlayPurchaseResult> {
  const planId = getPlanIdForGoogleProductId(purchase.productId);
  if (!planId || !purchase.purchaseToken) {
    return { ok: false, error: 'Não reconhecemos esse plano. Atualize o app e tente de novo.' };
  }

  try {
    await api.post('/google-play/validate-purchase', {
      productId: purchase.productId,
      purchaseToken: purchase.purchaseToken,
      planId,
    });
  } catch (err: unknown) {
    const msg = getApiErrorMessage(err, 'Não foi possível confirmar a compra. Tente novamente.');
    return { ok: false, error: msg };
  }

  try {
    await finishTransaction({ purchase, isConsumable: false });
  } catch {
    // A API já confirmou a compra no Google; confirmar de novo pode falhar.
  }
  return { ok: true, planId };
}

/** Assinaturas ativas na conta Google do aparelho, revalidadas na API. */
export async function restoreGooglePurchases(): Promise<GooglePlayPurchaseResult[]> {
  const purchases = await getAvailablePurchases();
  const results: GooglePlayPurchaseResult[] = [];
  for (const purchase of purchases ?? []) {
    if (!getPlanIdForGoogleProductId(purchase.productId)) continue;
    results.push(await validateGooglePurchaseWithBackend(purchase));
  }
  return results;
}

/** Abre "Pagamentos e assinaturas" do Google Play nessa assinatura. */
export async function openManageGoogleSubscriptions(planId?: string | null): Promise<void> {
  await deepLinkToSubscriptions({
    packageNameAndroid: PACKAGE_NAME,
    skuAndroid: (planId && getGoogleProductId(planId)) || null,
  });
}
